import { useFormContext } from 'react-hook-form'

import type { Budget as BudgetType, Category as CategoryType, Expense } from '@subaio/api-types/budget'
import { FormattedMessage } from '@subaio/components'

import { collectCategories } from 'lib/collectCategories'

import { Cell } from 'components/Lists'

import { Amount } from './Amount'
import { Category } from './Category'
import { Container } from './Container'
import { TitleRow } from './TitleRow'
import { Uncategorized } from './Uncategorized'

type Props = {
	amountCategorized: number
	amountCategorizedUserProvided: number | null
	categories: Array<CategoryType>
	currency: string
	fieldsBeingSubmitted: Array<string>
	uncategorized: Array<Expense>

	isUncategorizedEnabled: boolean

	onRecategorizeUncategorizedService: (args: {
		serviceId: string
		categoryId: string
		subCategoryId: string
	}) => Promise<BudgetType>
}

export const Budget = ({
	categories,
	amountCategorized,
	uncategorized,
	isUncategorizedEnabled,
	amountCategorizedUserProvided,
	currency,
	fieldsBeingSubmitted,
	onRecategorizeUncategorizedService,
}: Props) => {
	const { reset } = useFormContext()

	const categoriesMap = categories.flatMap(({ id, name, categories }) =>
		categories.map((subCategory) => ({
			label: `${name} - ${subCategory.name}`,
			value: { id, subCategoryId: subCategory.id },
		})),
	)

	return (
		<Container>
			<Uncategorized
				uncategorized={uncategorized}
				currency={currency}
				categoriesMap={categoriesMap}
				isUncategorizedEnabled={isUncategorizedEnabled}
				onRecategorize={async (props) => {
					const adjustedBudget = await onRecategorizeUncategorizedService(props)
					reset({ categories: collectCategories(adjustedBudget) })
				}}
			/>
			<hr />
			<TitleRow>
				<Cell shouldExpand>
					<FormattedMessage id='flow.budgetEditForm.expensesTitle' defaultMessage='Total expenses' />
				</Cell>
				<Cell>
					<Amount
						amount={amountCategorized}
						currency={currency}
						userProvidedAmount={amountCategorizedUserProvided}
						placeholderWidth='80px'
						isLoading={fieldsBeingSubmitted.some((id) => categories.some((category) => id.includes(category.id)))}
						type={(amountCategorizedUserProvided ?? amountCategorized) === 0 ? 'neutral' : 'negative'}
					/>
				</Cell>
			</TitleRow>
			{categories.map((category) => (
				<Category key={category.id} fieldsBeingSubmitted={fieldsBeingSubmitted} {...category} currency={currency} />
			))}
		</Container>
	)
}
